import React from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Sparkles } from "lucide-react";

type ReferenceType = "ALL" | "MEET" | "HOMEWORK" | "SURVEY" | "CUSTOM" | "APPOINTMENT";

interface Props {
  currentDate: Date;
  referenceType: ReferenceType;
  filterReferenceType?: ReferenceType;
  onChangeReference: (ref: ReferenceType) => void;
  onNavigate: (dir: "prev" | "next") => void;
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
}

const months = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

export default function CalendarHeader({
  currentDate,
  referenceType,
  filterReferenceType,
  onChangeReference,
  onNavigate,
  onToggleSidebar,
  sidebarOpen,
}: Props) {
  const handleAIPrompt = () => {
    window.dispatchEvent(new CustomEvent("toggleAIPanel"));
    setTimeout(() => {
      const message = "Takvim etkinliklerim neler?";
      window.dispatchEvent(new CustomEvent("sendAIMessage", { detail: { message } }));
    }, 300);
  };

  return (
    <div className="border-b border-gray-200 px-4 py-3 flex-shrink-0 bg-white shadow-sm">
      <div className="flex md:hidden flex-col gap-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => onNavigate("prev")}
            className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-gray-700 hover:text-blue-600">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="font-semibold text-lg text-gray-800">
            {months[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <button
            onClick={() => onNavigate("next")}
            className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-gray-700 hover:text-blue-600">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col items-center gap-2">
          <button
            onClick={handleAIPrompt}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 border border-purple-600 flex-shrink-0 transition-colors">
            <Sparkles className="w-3.5 h-3.5" />
            <span>AI'ya Sor</span>
          </button>
          {!filterReferenceType && (
            <div className="flex bg-gray-100 rounded-md p-1 text-xs overflow-x-auto">
              <button
                className={`px-2 py-1 rounded ${referenceType === "ALL" ? "bg-white shadow" : ""}`}
                onClick={() => onChangeReference("ALL")}>
                Tümü
              </button>
              <button
                className={`px-2 py-1 rounded ${referenceType === "HOMEWORK" ? "bg-white shadow" : ""}`}
                onClick={() => onChangeReference("HOMEWORK")}>
                Ödev
              </button>
              <button
                className={`px-2 py-1 rounded ${referenceType === "SURVEY" ? "bg-white shadow" : ""}`}
                onClick={() => onChangeReference("SURVEY")}>
                Anket
              </button>
              <button
                className={`px-2 py-1 rounded ${referenceType === "MEET" ? "bg-white shadow" : ""}`}
                onClick={() => onChangeReference("MEET")}>
                Toplantı
              </button>
              <button
                className={`px-2 py-1 rounded ${referenceType === "APPOINTMENT" ? "bg-white shadow" : ""}`}
                onClick={() => onChangeReference("APPOINTMENT")}>
                Randevu
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="hidden md:flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={handleAIPrompt}
            className="inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 border border-purple-600 flex-shrink-0 transition-colors">
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>AI'ya Sor</span>
          </button>
          {!filterReferenceType && (
            <div className="flex bg-gray-100 rounded-md p-1 text-xs">
              <button
                className={`px-2 py-1 rounded ${referenceType === "ALL" ? "bg-white shadow" : ""}`}
                onClick={() => onChangeReference("ALL")}>
                Tümü
              </button>
              <button
                className={`px-2 py-1 rounded ${referenceType === "HOMEWORK" ? "bg-white shadow" : ""}`}
                onClick={() => onChangeReference("HOMEWORK")}>
                Ödev
              </button>
              <button
                className={`px-2 py-1 rounded ${referenceType === "SURVEY" ? "bg-white shadow" : ""}`}
                onClick={() => onChangeReference("SURVEY")}>
                Anket
              </button>
              <button
                className={`px-2 py-1 rounded ${referenceType === "MEET" ? "bg-white shadow" : ""}`}
                onClick={() => onChangeReference("MEET")}>
                Toplantı
              </button>
              <button
                className={`px-2 py-1 rounded ${referenceType === "APPOINTMENT" ? "bg-white shadow" : ""}`}
                onClick={() => onChangeReference("APPOINTMENT")}>
                Randevu
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => onNavigate("prev")}
            className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-gray-700 hover:text-blue-600">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="font-semibold text-xl text-gray-800 min-w-[200px] text-center">
            {months[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <button
            onClick={() => onNavigate("next")}
            className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-gray-700 hover:text-blue-600">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={onToggleSidebar}
            className={`inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium flex-shrink-0 transition-colors ${
              sidebarOpen
                ? "text-white bg-blue-600 hover:bg-blue-700 border border-blue-600"
                : "text-gray-700 bg-white hover:bg-gray-50 border border-gray-300"
            }`}>
            <CalendarIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>Etkinlikler</span>
          </button>
        </div>
      </div>
    </div>
  );
}
